// YUVARAJ
import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  Table,
  Container,
  Row,
  Col,
  Card,
  Image,
  Spinner,
  Alert,
  Modal,
} from "react-bootstrap";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import DepartmentManagement from "../DepManagement/DepManagement";
import { getActionPermissions } from "../../Utils/ActionPermissionChecker";
import { doAuditLog } from "../../Utils/saveHistoryAction";
import { duration } from "moment";
import { showMyAlert } from "../../Utils/alertService";

const PayslipData = () => {
  // API endpoints
  const API_URL =
    "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollPayslip_PayrollPayslipData_PayrollPayslipLabelsList";
  const IMAGE_API = "https://api.dremerz.net/uploads/";
  const REG_API =
    "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers";

  // State for main tab navigation
  const [mainActiveTab, setMainActiveTab] = useState("payslipConfiguration");

  // State for form inputs
  const [companyInfo, setCompanyInfo] = useState({
    companyName: "",
    companyAddress: "",
    companyLogo: null,
    logoPreview: "",
  });

  const [labelData, setLabelData] = useState({
    company: "",
    employee: "",
    bank: "",
    earnings: "",
    deductions: "",
  });

  // State for all submitted data
  const [submittedData, setSubmittedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentEditItem, setCurrentEditItem] = useState(null);
  const [activeTab, setActiveTab] = useState("companyInfo");
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState("");

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState("");
  const [confirmModalItem, setConfirmModalItem] = useState("");

  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showLabelsModal, setShowLabelsModal] = useState(false);

  // Get user info from session storage
  const userRole = sessionStorage.getItem("role");
  const { canView, canAdd, canEdit, canDelete } = getActionPermissions(userRole);
  const userId = sessionStorage.getItem("userId");
  const userName = sessionStorage.getItem("userName");
  const userMobile = sessionStorage.getItem("userMobile");
  const companyKey = sessionStorage.getItem("companyKey");
  console.log(companyKey, "session")

  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = () => {
      const userId = sessionStorage.getItem("userMobile");
      if (!userId) {
        navigate("/signIn");
      }
    };

    checkAuth();
  }, [navigate]);

  // Function to update employee labels when adding new labels
  const updateEmployeeLabelsOnAdd = async (newLabel, isEarning) => {
    try {
      console.log(
        `Updating all employees: adding ${newLabel} as ${isEarning ? "earning" : "deduction"
        }`
      );

      // Fetch all employees
      const response = await axios.get(REG_API);
      const employees = response.data;

      console.log(`Found ${employees.length} employees to update`);

      // Process each employee
      for (const employee of employees) {
        let updatedEmployee = { ...employee };
        let needsUpdate = false;

        if (isEarning) {
          // Handle earnings
          let earnings = [];

          try {
            // Try to parse existing earnings if they exist
            if (employee.earnings && employee.earnings.trim() !== "") {
              earnings = JSON.parse(employee.earnings);

              // Ensure it's an array (handle case where it might be malformed)
              if (!Array.isArray(earnings)) {
                earnings = [];
              }
            }
          } catch (e) {
            console.error(
              `Error parsing earnings for employee ${employee.id}:`,
              e
            );
            earnings = [];
          }

          // Check if the earning already exists
          const earningExists = earnings.some(
            (item) =>
              typeof item === "object" &&
              item !== null &&
              item.hasOwnProperty(newLabel)
          );

          if (!earningExists) {
            // Add new earning with true value
            earnings.push({ [newLabel]: "true" });
            updatedEmployee.earnings = JSON.stringify(earnings);
            needsUpdate = true;
            console.log(
              `Added ${newLabel} to employee ${employee.id} earnings`
            );
          }
        } else {
          // Handle deductions
          let deductions = [];

          try {
            // Try to parse existing deductions if they exist
            if (employee.deductions && employee.deductions.trim() !== "") {
              deductions = JSON.parse(employee.deductions);

              // Ensure it's an array (handle case where it might be malformed)
              if (!Array.isArray(deductions)) {
                deductions = [];
              }
            }
          } catch (e) {
            console.error(
              `Error parsing deductions for employee ${employee.id}:`,
              e
            );
            deductions = [];
          }

          // Check if the deduction already exists
          const deductionExists = deductions.some(
            (item) =>
              typeof item === "object" &&
              item !== null &&
              item.hasOwnProperty(newLabel)
          );

          if (!deductionExists) {
            // Add new deduction with true value
            deductions.push({ [newLabel]: "true" });
            updatedEmployee.deductions = JSON.stringify(deductions);
            needsUpdate = true;
            console.log(
              `Added ${newLabel} to employee ${employee.id} deductions`
            );
          }
        }

        // Update employee if needed
        if (needsUpdate) {
          try {
            updatedEmployee.companyKey = companyKey;
            await axios.put(`${REG_API}/${employee.id}`, updatedEmployee);
            doAuditLog("Update", "Payslip Configuration", employee, updatedEmployee);
            console.log(`Successfully updated employee ${employee.id}`);
          } catch (error) {
            console.error(`Error updating employee ${employee.id}:`, error);
          }
        }
      }

      console.log(`Finished updating all employees for ${newLabel}`);
    } catch (error) {
      console.error("Error updating employee labels on add:", error);
      throw error;
    }
  };

  // Function to update employee labels when deleting labels (set to false)
  const updateEmployeeLabelsOnDelete = async (deletedLabel, isEarning) => {
    try {
      console.log(
        `Updating all employees: setting ${deletedLabel} to false for ${isEarning ? "earnings" : "deductions"
        }`
      );

      // Fetch all employees
      const response = await axios.get(REG_API);
      const employees = response.data;

      console.log(`Found ${employees.length} employees to update`);

      // Process each employee
      for (const employee of employees) {
        let updatedEmployee = { ...employee };
        let needsUpdate = false;

        if (isEarning) {
          // Handle earnings
          let earnings = [];

          try {
            // Try to parse existing earnings if they exist
            if (employee.earnings && employee.earnings.trim() !== "") {
              earnings = JSON.parse(employee.earnings);

              // Ensure it's an array
              if (!Array.isArray(earnings)) {
                earnings = [];
              }
            }
          } catch (e) {
            console.error(
              `Error parsing earnings for employee ${employee.id}:`,
              e
            );
            earnings = [];
          }

          // Find and update the deleted label to false
          const updatedEarnings = earnings.map((item) => {
            if (
              typeof item === "object" &&
              item !== null &&
              item.hasOwnProperty(deletedLabel)
            ) {
              needsUpdate = true;
              console.log(
                `Setting ${deletedLabel} to false for employee ${employee.id} earnings`
              );
              return { [deletedLabel]: "false" };
            }
            return item;
          });

          if (needsUpdate) {
            updatedEmployee.earnings = JSON.stringify(updatedEarnings);
          }
        } else {
          // Handle deductions
          let deductions = [];

          try {
            // Try to parse existing deductions if they exist
            if (employee.deductions && employee.deductions.trim() !== "") {
              deductions = JSON.parse(employee.deductions);

              // Ensure it's an array
              if (!Array.isArray(deductions)) {
                deductions = [];
              }
            }
          } catch (e) {
            console.error(
              `Error parsing deductions for employee ${employee.id}:`,
              e
            );
            deductions = [];
          }

          // Find and update the deleted label to false
          const updatedDeductions = deductions.map((item) => {
            if (
              typeof item === "object" &&
              item !== null &&
              item.hasOwnProperty(deletedLabel)
            ) {
              needsUpdate = true;
              console.log(
                `Setting ${deletedLabel} to false for employee ${employee.id} deductions`
              );
              return { [deletedLabel]: "false" };
            }
            return item;
          });

          if (needsUpdate) {
            updatedEmployee.deductions = JSON.stringify(updatedDeductions);
          }
        }

        // Update employee if needed
        if (needsUpdate) {
          try {
            updatedEmployee.companyKey = companyKey;
            await axios.put(`${REG_API}/${employee.id}`, updatedEmployee);
            doAuditLog("Update", "Payslip Configuration", employee, updatedEmployee);
          } catch (error) {
            console.error(`Error updating employee ${employee.id}:`, error);
          }
        }
      }

      console.log(
        `Finished updating all employees - set ${deletedLabel} to false`
      );
    } catch (error) {
      console.error("Error updating employee labels on delete:", error);
      throw error;
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch existing data on component mount

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_URL);
      const filteredData = response.data.filter(item => item.companyKey === companyKey);

      setSubmittedData(filteredData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      showMyAlert({
        type: "danger",
        title: "Error",
        message: "Failed to fetch data from server",
        duration: 2500,
      });
      setLoading(false);
    }
  };



  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      // Clean up blob URLs when component unmounts
      if (companyInfo.logoPreview && companyInfo.logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(companyInfo.logoPreview);
      }
    };
  }, [companyInfo.logoPreview]);

  // Handle company info changes
  const handleCompanyInfoChange = (e) => {
    const { name, value } = e.target;
    setCompanyInfo((prev) => ({ ...prev, [name]: value }));
  };

  // Handle label changes
  const handleLabelChange = (e) => {
    const { name, value } = e.target;
    setLabelData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle logo upload
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showMyAlert({
          type: "warning", // "success" | "danger" | "warning" | "info"
          title: "Error",
          message: "Please select a valid image file.",
          duration: 2500,
        });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showMyAlert({
          type: "warning", // "success" | "danger" | "warning" | "info"
          title: "Error",
          message: "File size must be less than 5MB.",
          duration: 2500,
        });
        return;
      }

      // Clean up previous preview URL to prevent memory leaks
      setCompanyInfo((prev) => {
        if (prev.logoPreview && prev.logoPreview.startsWith('blob:')) {
          URL.revokeObjectURL(prev.logoPreview);
        }
        return {
          ...prev,
          companyLogo: file,
          logoPreview: URL.createObjectURL(file),
        };
      });


    }
  };

  const saveDataToAPI = async (data) => {
    try {
      setLoading(true);
      const formData = new FormData();

      // Append all data to formData
      for (const key in data) {
        formData.append(key, data[key]);
      }

      // Always append companyKey for all submissions
      formData.append("companyKey", companyKey);

      const response = await axios.post(API_URL, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      const formDataObj = Object.fromEntries(formData.entries());
      doAuditLog("Add", "Payslip Configuration", "", formDataObj);
      // If this is an earnings or deductions label, update all employees
      if (data.sectionName === "Earnings") {
        await updateEmployeeLabelsOnAdd(data.value, true);
      } else if (data.sectionName === "Deductions") {
        await updateEmployeeLabelsOnAdd(data.value, false);
      }

      setSubmittedData((prev) => [...prev, response.data]);
      showMyAlert({
        type: "success", // "success" | "danger" | "warning" | "info"
        title: "Success",
        message: "Data saved successfully",
        duration: 2500,
      });
      fetchData();

      setLoading(false);
      return response.data;
    } catch (error) {
      console.error("Error saving data to server:", error);
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Error",
        message: "Failed to save data to server",
        duration: 2500,
      });
      setLoading(false);
      throw error;
    }
  };

  // API call to update data
  const updateDataInAPI = async (id, data, originalItem) => {
    try {
      setLoading(true);
      const formData = new FormData();

      // Append all data to formData
      for (const key in data) {
        formData.append(key, data[key]);
      }

      // Always append companyKey for updates too
      formData.append("companyKey", companyKey);

      const response = await axios.put(`${API_URL}/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      const formDataObj = Object.fromEntries(formData.entries());
      doAuditLog("Update", "Payslip Configuration", originalItem, formDataObj);

      // If this is an earnings or deductions label update, update all employees
      if (data.sectionName === "Earnings") {
        await updateEmployeeLabelsOnAdd(data.value, true);
      } else if (data.sectionName === "Deductions") {
        await updateEmployeeLabelsOnAdd(data.value, false);
      }

      setSubmittedData((prev) =>
        prev.map((item) => (item.id === id ? response.data : item))
      );
      showMyAlert({
        type: "success", // "success" | "danger" | "warning" | "info"
        title: "Success",
        message: "Data updated successfully",
        duration: 2500,
      });
      setLoading(false);
      return response.data;
    } catch (error) {
      console.error("Error updating data on server:", error);
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Error",
        message: "Failed to update data on server",
        duration: 2500,
      });
      setLoading(false);
      throw error;
    }
  };

  const deleteDataFromAPI = async (id) => {
    try {
      setLoading(true);
      const itemToDelete = submittedData.find((item) => item.id === id);

      // Before deleting, if it's earnings or deductions, update all employees to set the label to false
      if (
        itemToDelete &&
        (itemToDelete.sectionName === "Earnings" ||
          itemToDelete.sectionName === "Deductions")
      ) {
        const isEarning = itemToDelete.sectionName === "Earnings";
        await updateEmployeeLabelsOnDelete(itemToDelete.value, isEarning);
        console.log(
          `Updated all employees: set ${itemToDelete.value} to false`
        );
      }

      await axios.delete(`${API_URL}/${id}`);

      doAuditLog("Delete", "Payslip Configuration", itemToDelete, "");

      setSubmittedData((prev) => prev.filter((item) => item.id !== id));
      showMyAlert({
        type: "success", // "success" | "danger" | "warning" | "info"
        title: "Success",
        message: "Data deleted successfully",
        duration: 2500,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error deleting data from server:", error);
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Error",
        message: "Failed to delete data from server",
        duration: 2500,
      });
      setLoading(false);
    }
  };

  // Handle company info submission
  const handleCompanyInfoSubmit = async (e) => {
    e.preventDefault();

    if (
      !companyInfo.companyName.trim() &&
      !companyInfo.companyAddress.trim() &&
      !companyInfo.companyLogo
    ) {
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Error",
        message: "Please fill at least one field (Company Name, Address, or Logo)",
        duration: 2500,
      });
      return;
    }

    // Validate company name if provided
    if (companyInfo.companyName.trim() && companyInfo.companyName.trim().length < 2) {
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Error",
        message: "Company name must be at least 2 characters long",
        duration: 2500,
      });
      return;
    }

    // Validate company address if provided
    if (companyInfo.companyAddress.trim() && companyInfo.companyAddress.trim().length < 10) {
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Error",
        message: "Company address must be at least 10 characters long",
        duration: 2500,
      });
      return;
    }

    const companyData = {
      sectionName: "companyInfo",
      companyName: companyInfo.companyName.trim(),
      companyAddress: companyInfo.companyAddress.trim(),
      companyLogo: companyInfo.companyLogo,
    };

    try {
      await saveDataToAPI(companyData);
      // Reset form fields after successful submission
      setCompanyInfo({
        companyName: "",
        companyAddress: "",
        companyLogo: null,
        logoPreview: "",
      });
      // Reset the file input
      const fileInput = document.querySelector('input[type="file"][accept="image/*"]');
      if (fileInput) {
        fileInput.value = "";
      }
    } catch (error) {
      console.error("Error saving company info:", error);
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Error",
        message: "Failed to save company information. Please try again.",
        duration: 2500,
      });
    }
  };

  const handleLabelSubmit = async (sectionName) => {
    const value = labelData[sectionName.toLowerCase()].trim();
    if (!value) {
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Error",
        message: `Please enter a ${sectionName.toLowerCase()} label`,
        duration: 2500,
      });
      return;
    }

    // Validate label length
    if (value.length < 2) {
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Error",
        message: `${sectionName} label must be at least 2 characters long`,
        duration: 2500,
      });
      return;
    }

    if (value.length > 50) {
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Error",
        message: `${sectionName} label must be less than 50 characters long`,
        duration: 2500,
      });
      return;
    }

    // Check for duplicate labels
    const existingLabels = submittedData.filter(
      item => item.sectionName === sectionName &&
        item.value.toLowerCase() === value.toLowerCase()
        &&
        item.id !== currentEditItem?.id
    );

    if (existingLabels.length > 0) {
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Error",
        message: `${sectionName} label "${value}" already exists`,
        duration: 2500,
      });
      return;
    }

    const labelItem = {
      sectionName,
      value,
    };

    try {
      await saveDataToAPI(labelItem); // Pass true to skip history logging
      setLabelData((prev) => ({ ...prev, [sectionName.toLowerCase()]: "" }));
    } catch (error) {
      console.error("Error saving label:", error);
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Error",
        message: "Failed to save label. Please try again.",
        duration: 2500,
      });
    }
  };

  // Handle item edit
  const handleEdit = (item) => {
    setCurrentEditItem(item);
    if (item.sectionName === "companyInfo") {
      setCompanyInfo({
        companyName: item.companyName || "",
        companyAddress: item.companyAddress || "",
        companyLogo: null,
        logoPreview: item.companyLogo ? `${IMAGE_API}${item.companyLogo}` : "",
      });
    } else {
      setLabelData((prev) => ({
        ...prev,
        [item.sectionName.toLowerCase()]: item.value || "",
      }));
    }
    setShowEditModal(true);
  };

  // Handle edit submission
  const handleEditSubmit = async () => {
    if (!currentEditItem) return;

    try {
      if (currentEditItem.sectionName === "companyInfo") {
        if (
          !companyInfo.companyName.trim() &&
          !companyInfo.companyAddress.trim() &&
          !companyInfo.companyLogo
        ) {
          showMyAlert({
            type: "danger", // "success" | "danger" | "warning" | "info"
            title: "Error",
            message: "Please fill at least one field (Company Name, Address, or Logo)",
            duration: 2500,
          });
          return;
        }

        // Validate company name if provided
        if (companyInfo.companyName.trim() && companyInfo.companyName.trim().length < 2) {
          showMyAlert({
            type: "danger", // "success" | "danger" | "warning" | "info"
            title: "Error",
            message: "Company name must be at least 2 characters long",
            duration: 2500,
          });
          return;
        }

        // Validate company address if provided
        if (companyInfo.companyAddress.trim() && companyInfo.companyAddress.trim().length < 10) {
          showMyAlert({
            type: "danger", // "success" | "danger" | "warning" | "info"
            title: "Error",
            message: "Company address must be at least 10 characters long",
            duration: 2500,
          });
          return;
        }

        const companyData = {
          sectionName: "companyInfo",
          companyName: companyInfo.companyName.trim(),
          companyAddress: companyInfo.companyAddress.trim(),
          // Only include companyLogo if a new file is selected, otherwise keep existing
          companyLogo: companyInfo.companyLogo || currentEditItem.companyLogo,
        };

        await updateDataInAPI(currentEditItem.id, companyData, currentEditItem);
        // Reset form fields after successful update
        setCompanyInfo({
          companyName: "",
          companyAddress: "",
          companyLogo: null,
          logoPreview: "",
        });
        // Reset the file input in modal
        const modalFileInput = document.querySelector('.modal input[type="file"][accept="image/*"]');
        if (modalFileInput) {
          modalFileInput.value = "";
        }
      } else {
        const labelValue =
          labelData[currentEditItem.sectionName.toLowerCase()].trim();
        if (!labelValue) {
          showMyAlert({
            type: "danger", // "success" | "danger" | "warning" | "info"

            title: "Error",
            message: `Please enter a ${currentEditItem.sectionName.toLowerCase()} label`,
            duration: 2500,
          });
          return;
        }

        // Validate label length
        if (labelValue.length < 2) {
          showMyAlert({
            type: "danger", // "success" | "danger" | "warning" | "info"
            title: "Error",
            message: `${currentEditItem.sectionName} label must be at least 2 characters long`,
            duration: 2500,
          });
          return;
        }

        if (labelValue.length > 50) {
          showMyAlert({
            type: "danger", // "success" | "danger" | "warning" | "info"

            title: "Error",
            message: `${currentEditItem.sectionName} label must be less than 50 characters long`,
            duration: 2500,
          });
          return;
        }

        // Check for duplicate labels
        const existingLabels = submittedData.filter(
          item => item.sectionName === currentEditItem.sectionName &&
            item.value.toLowerCase() === labelValue.toLowerCase() &&
            item.id !== currentEditItem.id
        );

        if (existingLabels.length > 0) {
          showMyAlert({
            type: "danger", // "success" | "danger" | "warning" | "info"

            title: "Error",
            message: `${currentEditItem.sectionName} label "${labelValue}" already exists`,
            duration: 2500,
          });
          return;
        }

        const labelItem = {
          sectionName: currentEditItem.sectionName,
          value: labelValue,
        };

        await updateDataInAPI(currentEditItem.id, labelItem, currentEditItem);
        setLabelData((prev) => ({
          ...prev,
          [currentEditItem.sectionName.toLowerCase()]: "",
        }));
      }
      fetchData()
      setShowEditModal(false);
      setCurrentEditItem(null);

    } catch (error) {
      console.error("Error updating item:", error);
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"

        title: "Error",
        message: "Failed to update item. Please try again.",
        duration: 2500,
      });
    }
  };

  const handleDelete = async (id) => {
    const itemToDelete = submittedData.find((item) => item.id === id);
    const isEarningOrDeduction =
      itemToDelete &&
      (itemToDelete.sectionName === "Earnings" ||
        itemToDelete.sectionName === "Deductions");

    const confirmMessage = isEarningOrDeduction
      ? `Are you sure you want to delete "${itemToDelete.value
      }" from ${itemToDelete.sectionName.toLowerCase()}?\n\nThis will also set this label to 'FALSE' for all employees in their payslip records.`
      : "Are you sure you want to delete this item?";

    setConfirmModalMessage(confirmMessage);
    setConfirmModalItem(id);
    setShowConfirmModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteDataFromAPI(confirmModalItem);
    } catch (error) {
      console.error("Error deleting item:", error);
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"

        title: "Error",
        message: "Failed to delete item. Please try again.",
        duration: 2500,
      })
    } finally {
      setConfirmModalMessage("");
      setConfirmModalItem("");
      setShowConfirmModal(false);
    }
  }

  // Handle image modal opening
  const handleImageClick = (imageUrl) => {
    setCurrentImageUrl(imageUrl);
    setShowImageModal(true);
  };

  // Group data by section for display
  const groupedData = submittedData.reduce((acc, item) => {
    const section = item.sectionName;
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(item);
    return acc;
  }, {});

  // Get section names for tabs
  const sectionNames =
    Object.keys(groupedData).length > 0
      ? Object.keys(groupedData)
      : [
        "companyInfo",
        "Company",
        "Employee",
        "Bank",
        "Earnings",
        "Deductions",
      ];

  const isMobile = window.innerWidth < 768;

  return (
    <Container className="container-fluid mt-3">
      <div className="row align-items-center mb-2 border rounded bg-light py-2 mx-2 mt-2">

        {/* Heading */}
        <div className="col-12 col-md-6 mb-2 mb-md-0">
          <h5 className="mb-0 d-flex align-items-center 
                   justify-content-center justify-content-md-start gap-2">
            Payslip Configuration
          </h5>
        </div>

        {/* Buttons */}
        <div className="col-12 col-md-6 d-flex 
                  justify-content-center justify-content-md-end 
                  gap-2 flex-wrap">

          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowCompanyModal(true)}
            className="py-2 px-3"
          >
            Add Company Information
          </Button>

          <Button
            variant="success"
            size="sm"
            onClick={() => setShowLabelsModal(true)}
            className="py-2 px-3"
          >
            Add Labels Configuration
          </Button>

        </div>
      </div>


      {/* Tab Content */}
      <div className="tab-content-wrapper">
        {mainActiveTab === "payslipConfiguration" ? (
          <>

            {/* Display Section */}
            <Card className="shadow-sm border-0 mt-3">
              <Card.Header
                className="border-0"
                id="text-white-important"
              >
                <h5 className="mb-0">
                  Payslip Configuration Data
                </h5>
              </Card.Header>
              <Card.Body>
                {/* Section Tabs */}
                <div className="mb-4">
                  <nav>
                    <div className="nav nav-tabs" id="nav-tab" role="tablist">
                      {sectionNames.map((section, index) => (
                        <button
                          key={section}
                          className={`nav-link ${activeTab === section ? "active" : ""
                            }`}
                          id={`nav-${section}-tab`}
                          data-bs-toggle="tab"
                          data-bs-target={`#nav-${section}`}
                          type="button"
                          role="tab"
                          aria-controls={`nav-${section}`}
                          aria-selected={activeTab === section}
                          onClick={() => setActiveTab(section)}
                        >
                          {section === "companyInfo" ? "Company Info" : section}
                          {groupedData[section] &&
                            ` (${groupedData[section].length})`}
                        </button>
                      ))}
                    </div>
                  </nav>
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Loading data...</p>
                  </div>
                ) : (
                  <div className="tab-content" id="nav-tabContent">
                    {sectionNames.map((section) => (
                      <div
                        key={section}
                        className={`tab-pane fade ${activeTab === section ? "show active" : ""
                          }`}
                        id={`nav-${section}`}
                        role="tabpanel"
                        aria-labelledby={`nav-${section}-tab`}
                      >
                        {groupedData[section] ? (
                          section === "companyInfo" ? (
                            isMobile ? (
                              <div>
                                {groupedData[section].map((item) => (
                                  <div key={item.id} className="mb-3">
                                    <div
                                      className="card shadow-sm"
                                      style={{
                                        textAlign: "left",
                                        borderRadius: "12px",
                                      }}
                                    >
                                      <div className="card-body">
                                        <table className="table table-striped table-bordered mb-2">
                                          <tbody>
                                            <tr>
                                              <td className="fw-bold">ID</td>
                                              <td>{item.id}</td>
                                            </tr>
                                            <tr>
                                              <td className="fw-bold">
                                                Company Name
                                              </td>
                                              <td>
                                                {item.companyName || "N/A"}
                                              </td>
                                            </tr>
                                            <tr>
                                              <td className="fw-bold">
                                                Company Address
                                              </td>
                                              <td
                                                className="text-break"
                                                style={{ maxWidth: "200px" }}
                                              >
                                                {item.companyAddress || "N/A"}
                                              </td>
                                            </tr>
                                            <tr>
                                              <td className="fw-bold">
                                                Company Logo
                                              </td>
                                              <td className="text-center">
                                                {item.companyLogo ? (
                                                  <Image
                                                    src={`${IMAGE_API}${item.companyLogo}`}
                                                    thumbnail
                                                    style={{
                                                      maxHeight: "50px",
                                                      cursor: "pointer",
                                                    }}
                                                    onClick={() =>
                                                      handleImageClick(`${IMAGE_API}${item.companyLogo}`)
                                                    }
                                                    title="Click to view in modal"
                                                  />
                                                ) : (
                                                  "N/A"
                                                )}
                                              </td>
                                            </tr>
                                          </tbody>
                                        </table>
                                        {(canDelete || canEdit) && <div className="d-flex gap-2">
                                          {canEdit && <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="me-2"
                                            onClick={() => handleEdit(item)}
                                            disabled={loading}
                                          >
                                            <i className="bi bi-pencil"></i>{" "}
                                            Edit
                                          </Button>}
                                          {canDelete && <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() =>
                                              handleDelete(item.id)
                                            }
                                            disabled={loading}
                                          >
                                            <i className="bi bi-trash"></i>{" "}
                                            Delete
                                          </Button>}
                                        </div>}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="table-responsive">
                                <Table striped bordered hover className="mb-0">
                                  <thead>
                                    <tr>
                                      <th id="text-white-important">ID</th>
                                      <th id="text-white-important">Company Name</th>
                                      <th id="text-white-important">Company Address</th>
                                      <th id="text-white-important">Company Logo</th>
                                      {(canEdit || canDelete) && <th id="text-white-important">Actions</th>}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {groupedData[section].map((item) => (
                                      <tr key={item.id}>
                                        <td>{item.id}</td>
                                        <td>{item.companyName || "N/A"}</td>
                                        <td
                                          className="text-break"
                                          style={{ maxWidth: "200px" }}
                                        >
                                          {item.companyAddress || "N/A"}
                                        </td>
                                        <td className="text-center">
                                          {item.companyLogo ? (
                                            <Image
                                              src={`${IMAGE_API}${item.companyLogo}`}
                                              thumbnail
                                              style={{
                                                maxHeight: "50px",
                                                cursor: "pointer",
                                              }}
                                              onClick={() =>
                                                handleImageClick(`${IMAGE_API}${item.companyLogo}`)
                                              }
                                              title="Click to view in modal"
                                            />
                                          ) : (
                                            "N/A"
                                          )}
                                        </td>
                                        {(canDelete || canEdit) && <td className="text-nowrap">
                                          {canEdit && <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="me-2"
                                            onClick={() => handleEdit(item)}
                                            disabled={loading}
                                          >
                                            <i className="bi bi-pencil"></i>{" "}
                                            Edit
                                          </Button>}
                                          {canDelete && <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() =>
                                              handleDelete(item.id)
                                            }
                                            disabled={loading}
                                          >
                                            <i className="bi bi-trash"></i>{" "}
                                            Delete
                                          </Button>}
                                        </td>}
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            )
                          ) : (
                            <div className="table-responsive">
                              <Table striped bordered hover className="mb-0">
                                <thead className="table-dark">
                                  <tr>
                                    <th id="text-white-important">ID</th>
                                    <th id="text-white-important">Label Value</th>
                                    {(canEdit || canDelete) && <th id="text-white-important">Actions</th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {groupedData[section].map((item) => (
                                    <tr key={item.id}>
                                      <td>{item.id}</td>
                                      <td className="fw-bold">
                                        {item.value || "N/A"}
                                      </td>
                                      {(canEdit || canDelete) && <td className="text-nowrap">
                                        {canEdit && <Button
                                          variant="outline-primary"
                                          size="sm"
                                          className="me-2"
                                          onClick={() => handleEdit(item)}
                                          disabled={loading}
                                        >
                                          <i className="bi bi-pencil"></i> Edit
                                        </Button>}
                                        {canDelete && <Button
                                          variant="outline-danger"
                                          size="sm"
                                          onClick={() => handleDelete(item.id)}
                                          disabled={loading}
                                          title={
                                            section === "Earnings" ||
                                              section === "Deductions"
                                              ? "⚠️ This will set the label to 'FALSE' for all employees"
                                              : "Delete this item"
                                          }
                                        >
                                          <i className="bi bi-trash"></i> Delete
                                          {(section === "Earnings" ||
                                            section === "Deductions") && (
                                              <i className="bi bi-exclamation-triangle ms-1 text-warning"></i>
                                            )}
                                        </Button>}
                                      </td>}
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          )
                        ) : (
                          <div className="alert alert-info d-flex align-items-center">
                            <i className="bi bi-info-circle me-2"></i>
                            No data available for{" "}
                            <strong className="ms-1">
                              {section === "companyInfo"
                                ? "Company Info"
                                : section}
                            </strong>{" "}
                            section. Use the forms above to add data.
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

              </Card.Body>
            </Card>
          </>
        ) : (
          <>
            <Card className="shadow-sm border-0">
              <Card.Body className="p-0">
                <DepartmentManagement />
              </Card.Body>
            </Card>
          </>
        )}
      </div>

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setCurrentEditItem(null);
          // Reset form fields when modal is closed
          setCompanyInfo({
            companyName: "",
            companyAddress: "",
            companyLogo: null,
            logoPreview: "",
          });
          setLabelData({
            company: "",
            employee: "",
            bank: "",
            earnings: "",
            deductions: "",
          });
        }}
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <i className="bi bi-pencil-square me-2"></i>
            Edit{" "}
            {currentEditItem?.sectionName === "companyInfo"
              ? "Company Information"
              : currentEditItem?.sectionName + " Label"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentEditItem?.sectionName === "companyInfo" ? (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  <i className="bi bi-building me-2"></i>
                  Company Name
                </Form.Label>
                <Form.Control
                  type="text"
                  name="companyName"
                  value={companyInfo.companyName}
                  onChange={handleCompanyInfoChange}
                  placeholder="Enter company name"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  <i className="bi bi-geo-alt me-2"></i>
                  Company Address
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="companyAddress"
                  value={companyInfo.companyAddress}
                  onChange={handleCompanyInfoChange}
                  placeholder="Enter company address"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  <i className="bi bi-image me-2"></i>
                  Company Logo
                </Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                />
                <div className="d-flex mt-3 gap-3">
                  {companyInfo.logoPreview && (
                    <div className="text-center">
                      <div className="border border-primary rounded p-2">
                        <Image
                          src={companyInfo.logoPreview}
                          thumbnail
                          style={{ maxHeight: "100px" }}
                          className="border-0"
                        />
                      </div>
                      <small className="text-primary fw-bold mt-1 d-block">
                        <i className="bi bi-arrow-up-circle me-1"></i>
                        New logo
                      </small>
                    </div>
                  )}
                  {currentEditItem?.companyLogo && (
                    <div className="text-center">
                      <div className="rounded p-2">
                        <Image
                          src={`${IMAGE_API}${currentEditItem.companyLogo}`}
                          thumbnail
                          style={{ maxHeight: "100px" }}
                          className="border-0"
                        />
                      </div>
                      <small className="text-muted mt-1 d-block">
                        <i className="bi bi-clock-history me-1"></i>
                        Current logo
                      </small>
                    </div>
                  )}
                </div>
              </Form.Group>
            </Form>
          ) : (
            <div>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  <i className="bi bi-tag me-2"></i>
                  Label Text
                </Form.Label>
                <Form.Control
                  type="text"
                  name={currentEditItem?.sectionName.toLowerCase()}
                  value={
                    labelData[currentEditItem?.sectionName.toLowerCase()] || ""
                  }
                  onChange={handleLabelChange}
                  placeholder={`Enter ${currentEditItem?.sectionName.toLowerCase()} label`}
                />
              </Form.Group>

              {(currentEditItem?.sectionName === "Earnings" ||
                currentEditItem?.sectionName === "Deductions") && (
                  <Alert variant="warning" className="d-flex align-items-start">
                    <i className="bi bi-exclamation-triangle-fill me-2 mt-1"></i>
                    <div>
                      <strong>Important:</strong> Updating this label will
                      automatically update all employee records with the new label
                      value. This action affects all employees in the system.
                      <hr className="my-2" />
                      <small className="text-muted">
                        <i className="bi bi-info-circle me-1"></i>
                        Current label: "<strong>{currentEditItem?.value}</strong>"
                      </small>
                    </div>
                  </Alert>
                )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <div>
            {(currentEditItem?.sectionName === "Earnings" ||
              currentEditItem?.sectionName === "Deductions") && (
                <small className="text-muted">
                  <i className="bi bi-people me-1"></i>
                  This will update all employee records
                </small>
              )}
          </div>
          <div>
            <Button
              variant="secondary"
              onClick={() => setShowEditModal(false)}
              className="me-2"
            >
              <i className="bi bi-x-circle me-1"></i>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleEditSubmit}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span className="ms-2">Updating...</span>
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-1"></i>
                  Update
                </>
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Loading Overlay */}
      {loading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 9999,
          }}
        >
          <div className="bg-white p-4 rounded shadow text-center">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <h5 className="mb-2">Processing...</h5>
            <p className="text-muted mb-0">
              Please wait while we update the records
            </p>
          </div>
        </div>
      )}


      {/* Company Information Modal */}
      <Modal
        show={showCompanyModal}
        onHide={() => {
          setShowCompanyModal(false);
          // Reset form when closing
          setCompanyInfo({
            companyName: "",
            companyAddress: "",
            companyLogo: null,
            logoPreview: "",
          });
        }}
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="">
          <Modal.Title>
            Add Company Information
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCompanyInfoSubmit}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Company Name
              </Form.Label>
              <Form.Control
                type="text"
                name="companyName"
                value={companyInfo.companyName}
                onChange={handleCompanyInfoChange}
                placeholder="Enter company name"
                disabled={loading}
              // className="border-secondary"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Company Address
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="companyAddress"
                value={companyInfo.companyAddress}
                onChange={handleCompanyInfoChange}
                placeholder="Enter company address"
                disabled={loading}
              // className="border-secondary"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Company Logo
              </Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={loading}
              // className="border-secondary"
              />
              {companyInfo.logoPreview && (
                <div className="mt-3 text-center">
                  <Image
                    src={companyInfo.logoPreview}
                    thumbnail
                    className="mt-2 border-primary"
                    style={{ maxHeight: "100px" }}
                  />
                  <p className="text-muted small mt-1">
                    Logo preview
                  </p>
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCompanyModal(false)}
            className="me-2"
          >
            <i className="bi bi-x-circle me-1"></i>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCompanyInfoSubmit}
            disabled={
              loading ||
              (!companyInfo.companyName.trim() &&
                !companyInfo.companyAddress.trim() &&
                !companyInfo.companyLogo)
            }
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span className="ms-2">Saving...</span>
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-1"></i>
                Save Company Info
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>


      {/* Labels Configuration Modal */}
      <Modal
        show={showLabelsModal}
        onHide={() => {
          setShowLabelsModal(false);
          // Reset form when closing
          setLabelData({
            company: "",
            employee: "",
            bank: "",
            earnings: "",
            deductions: "",
          });
        }}
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="">
          <Modal.Title>
            Add Labels Configuration
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Company Label
              </Form.Label>
              <div className="d-flex">
                <Form.Select
                  name="company"
                  value={labelData.company}
                  onChange={handleLabelChange}
                  disabled={loading}
                // className="border-primary"
                >
                  <option value="">Select Company Label</option>
                  <option value="Payslip Period">Payslip Period</option>
                </Form.Select>
                {canAdd && <Button
                  variant="primary"
                  onClick={() => {
                    handleLabelSubmit("Company");
                    setLabelData(prev => ({ ...prev, company: "" }));
                  }}
                  disabled={loading || !labelData.company.trim()}
                  className="ms-2"
                >
                  Add
                </Button>}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Employee Label
              </Form.Label>
              <div className="d-flex">
                <Form.Select
                  name="employee"
                  value={labelData.employee}
                  onChange={handleLabelChange}
                  disabled={loading}
                // className="border-primary"
                >
                  <option value="">Select Employee Label</option>
                  <option value="Employee Name">Employee Name</option>
                  <option value="Employee ID">Employee ID</option>
                  <option value="Pay Period">Pay Period</option>
                  <option value="Designation">Designation</option>
                  <option value="Email">Email</option>
                  <option value="Mobile Number">Mobile Number</option>
                </Form.Select>
                {canAdd && <Button
                  variant="primary"
                  onClick={() => {
                    handleLabelSubmit("Employee");
                    setLabelData(prev => ({ ...prev, employee: "" }));
                  }}
                  disabled={loading || !labelData.employee.trim()}
                  className="ms-2"
                >
                  Add
                </Button>}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Bank Label
              </Form.Label>
              <div className="d-flex">
                <Form.Select
                  name="bank"
                  value={labelData.bank}
                  onChange={handleLabelChange}
                  disabled={loading}
                // className="border-primary"
                >
                  <option value="">Select Bank Label</option>
                  <option value="Bank Name">Bank Name</option>
                  <option value="Account No">Account No</option>
                  <option value="IFSC Code">IFSC Code</option>
                  <option value="Branch Name">Branch Name</option>
                </Form.Select>
                {canAdd && <Button
                  variant="primary"
                  onClick={() => {
                    handleLabelSubmit("Bank");
                    setLabelData(prev => ({ ...prev, bank: "" }));
                  }}
                  disabled={loading || !labelData.bank.trim()}
                  className="ms-2"
                >
                  Add
                </Button>}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Earnings Label
              </Form.Label>
              <div className="d-flex">
                <Form.Select
                  name="earnings"
                  value={labelData.earnings}
                  onChange={handleLabelChange}
                  disabled={loading}
                // className="border-primary"
                >
                  <option value="">Select or enter custom earning</option>
                  <option value="Basic">Basic Salary</option>
                  <option value="Incentive">Incentive</option>
                  <option value="Bonus">Bonus</option>
                  <option value="custom">Other</option>
                </Form.Select>
                {labelData.earnings === "custom" && (
                  <Form.Control
                    type="text"
                    placeholder="Enter custom earning label"
                    onChange={(e) => setLabelData(prev => ({ ...prev, earnings: e.target.value }))}
                    className="ms-2"
                    autoFocus
                  />
                )}
                {canAdd && <Button
                  variant="primary"
                  onClick={() => {
                    handleLabelSubmit("Earnings");
                    setLabelData(prev => ({ ...prev, earnings: "" }));
                  }}
                  disabled={loading || !labelData.earnings.trim() || labelData.earnings === "custom"}
                  className="ms-2"
                >
                  Add
                </Button>}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Deductions Label
              </Form.Label>
              <div className="d-flex">
                <Form.Select
                  name="deductions"
                  value={labelData.deductions}
                  onChange={handleLabelChange}
                  disabled={loading}
                // className="border-primary"
                >
                  <option value="">Select or enter custom deduction</option>
                  <option value="PF">Provident Fund (PF)</option>
                  <option value="Loss of pay">Loss of pay</option>
                  <option value="Income Tax">Income Tax</option>
                  <option value="custom">Other</option>
                </Form.Select>
                {labelData.deductions === "custom" && (
                  <Form.Control
                    type="text"
                    placeholder="Enter custom deduction label"
                    onChange={(e) => setLabelData(prev => ({ ...prev, deductions: e.target.value }))}
                    className="ms-2"
                    autoFocus
                  />
                )}
                {canAdd && <Button
                  variant="primary"
                  onClick={() => {
                    handleLabelSubmit("Deductions");
                    setLabelData(prev => ({ ...prev, deductions: "" }));
                  }}
                  disabled={loading || !labelData.deductions.trim() || labelData.deductions === "custom"}
                  className="ms-2"
                >
                  Add
                </Button>}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowLabelsModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Image Modal */}
      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Company Logo
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {currentImageUrl ? (
            <img
              src={currentImageUrl}
              alt="Company Logo"
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                borderRadius: "8px",
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
              }}
              onError={() => {
                showMyAlert({
                  type: "danger",
                  title: "Error",
                  message: "Failed to load image. Please try again later.",
                  duration: 2500,
                });
                setShowImageModal(false);
              }}
            />
          ) : (
            <div className="text-center py-5">
              <i className="bi bi-image text-muted" style={{ fontSize: "3rem" }}></i>
              <p className="text-muted mt-3">No image available</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowImageModal(false)}
          >
            <i className="bi bi-x-circle me-1"></i>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showConfirmModal}
        onHide={() => { setShowConfirmModal(false); setConfirmModalMessage("") }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{confirmModalMessage}</p>
          <p className="text-danger">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowConfirmModal(false); setConfirmModalMessage("") }}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Confirm Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PayslipData;
